import type { Scene } from "@babylonjs/core/scene";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";

/**
 * PROTOTYPE (look "2d"): small pixel-art surfaces drawn in code, sampled with
 * hard edges, for the HD-2D arena. About 16 texels per metre, like the sprites.
 */
export type SurfaceKind = "floor" | "wall" | "brick" | "wood" | "weatheredWood" | "slate" | "trim" | "sand" | "stone" | "arches" | "dark" | "rock" | "ash" | "iron" | "bark" | "water";

type RGB = [number, number, number];

/** Deterministic noise: the same arena looks the same on every load and every device. */
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

function shade(c: RGB, d: number): RGB {
  return [c[0] + d, c[1] + d, c[2] + d].map((v) => Math.max(0, Math.min(255, Math.round(v)))) as RGB;
}

interface Painter {
  size: number;
  paint(x: number, y: number, random: () => number): RGB;
}

const PAINTERS: Record<SurfaceKind, Painter> = {
  // Worn flagstones, 16 px tiles, dark mortar with a few moss specks.
  floor: {
    size: 64,
    paint(x, y, r) {
      const tx = Math.floor(x / 16);
      const ty = Math.floor(y / 16);
      const lx = x % 16;
      const ly = y % 16;
      if (lx === 0 || ly === 0) return r() < 0.12 ? [74, 96, 52] : [70, 62, 58];
      const tint = (((tx * 7 + ty * 13) % 5) - 2) * 6;
      let c: RGB = shade([176, 166, 146], tint + (r() - 0.5) * 14);
      if (lx === 1 || ly === 1) c = shade(c, 14);
      if (lx === 15 || ly === 15) c = shade(c, -16);
      if (r() < 0.01) c = shade(c, -40);
      return c;
    },
  },
  // Dark earth and rock under the floor edge.
  wall: {
    size: 32,
    paint(x, y, r) {
      const band = Math.floor(y / 8) % 2 === 0 ? 0 : -8;
      return shade([82, 70, 78], band + (r() - 0.5) * 22);
    },
  },
  // Staggered stone bricks for the pillars.
  brick: {
    size: 32,
    paint(x, y, r) {
      const row = Math.floor(y / 8);
      const lx = (x + (row % 2) * 8) % 16;
      const ly = y % 8;
      if (lx === 0 || ly === 0) return [58, 54, 70];
      let c: RGB = shade([128, 124, 146], (((row * 5 + Math.floor((x + (row % 2) * 8) / 16) * 3) % 4) - 1.5) * 7 + (r() - 0.5) * 12);
      if (ly === 1) c = shade(c, 16);
      if (ly === 7) c = shade(c, -18);
      return c;
    },
  },
  // Vertical planks with grain.
  slate: {
    size:64,
    paint(x,y,r){
      const row=Math.floor(y/12),edge=(x+(row%2)*8)%16;
      if(y%12<2 || edge<1)return [35,31,27];
      const tint=((Math.floor((x+(row%2)*8)/16)*7+row*3)%5)*5;
      const moss=(x*3+y*7)%71===0;
      return shade(moss?[76,83,48]:[86,78,65],tint+(y%12===2?18:0)+(r()-.5)*12);
    },
  },
  weatheredWood:{
    size:64,
    paint(x,y,r){
      const plank=Math.floor(x/16),edge=x%16;
      if(edge<2)return [34,31,27];
      const grain=Math.sin(y*.39+plank*8+edge*.7)*3;
      const tint=[-3,4,-7,1][plank]!;
      let c=shade([82,73,61],tint+grain+(r()-.5)*9);
      if(edge===2)c=shade(c,9);
      if(edge===15)c=shade(c,-11);
      if((edge===5 || edge===12) && (y===7 || y===55))return [37,36,32];
      if((y*3+plank*17)%59===0 && edge>6 && edge<11)c=shade(c,-14);
      return c;
    },
  },
  wood: {
    size: 32,
    paint(x, y, r) {
      const plank = Math.floor(x / 8);
      if (x % 8 === 0) return [70, 44, 30];
      const grain = Math.sin((y + plank * 11) * 0.7 + (x % 8)) * 6;
      let c: RGB = shade([170, 118, 74], ((plank * 3) % 3) * 8 - 8 + grain + (r() - 0.5) * 10);
      if ((y + plank * 9) % 32 === 0) c = shade(c, -36);
      return c;
    },
  },
  // Carved gold-stone trim along the arena edge.
  trim: {
    size: 16,
    paint(x, y, r) {
      if (y === 0 || y === 15) return [96, 70, 34];
      const stripe = (x + y) % 8 < 4 ? 14 : -6;
      return shade([214, 168, 82], stripe + (r() - 0.5) * 10);
    },
  },
  // Raked arena sand: warm, grainy, faint rake lines and a few pebbles.
  sand: {
    size: 64,
    paint(x, y, r) {
      let c: RGB = shade([206, 172, 120], (r() - 0.5) * 18 + (y % 16 === 0 ? -10 : 0));
      if (((x + y * 3) % 23 === 0 && r() < 0.5) || r() < 0.006) c = shade(c, -34);
      if (r() < 0.01) c = shade(c, 26);
      return c;
    },
  },
  // Travertine blocks of the stands, wide and shallow.
  stone: {
    size: 32,
    paint(x, y, r) {
      const row = Math.floor(y / 8);
      const lx = (x + (row % 2) * 12) % 24;
      if (lx === 0 || y % 8 === 0) return [96, 82, 74];
      let c: RGB = shade([178, 160, 136], (((row * 7) % 3) - 1) * 6 + (r() - 0.5) * 16);
      if (y % 8 === 1) c = shade(c, 14);
      if (r() < 0.03) c = shade(c, -24);
      return c;
    },
  },
  // The top of the facade: arches with deep shadow, like the real Colosseum.
  arches: {
    size: 32,
    paint(x, y, r) {
      const lx = x % 16;
      const inArch = lx >= 3 && lx <= 12 && y >= 8 && (y > 12 || (lx - 7.5) ** 2 + (y - 12) ** 2 <= 22);
      if (inArch) return shade([40, 28, 34], (r() - 0.5) * 8);
      if (y < 3) return shade([150, 132, 112], (r() - 0.5) * 10);
      return shade([170, 150, 126], (lx === 0 ? -30 : 0) + (r() - 0.5) * 14);
    },
  },
  // Basalt: dark, cracked, a few warm highlights from the fires.
  rock: {
    size: 32,
    paint(x, y, r) {
      const cell = Math.floor(x / 8) * 7 + Math.floor(y / 6) * 13;
      let c: RGB = shade([78, 64, 62], ((cell % 5) - 2) * 6 + (r() - 0.5) * 16);
      if (x % 8 === 0 || y % 6 === 0) c = shade(c, -26);
      if (y % 6 === 1) c = shade(c, 12);
      if (r() < 0.012) c = [150, 74, 40];
      return c;
    },
  },
  // Scorched ground of Hell: ash and dark earth, glowing cracks.
  ash: {
    size: 64,
    paint(x, y, r) {
      let c: RGB = shade([92, 74, 66], (r() - 0.5) * 20 + (((x >> 3) + (y >> 3)) % 3) * 4);
      const crack = Math.abs(Math.sin(x * 0.21 + Math.sin(y * 0.13) * 2.4) * 18 - 9) < 0.35 || Math.abs(Math.sin(y * 0.19 + x * 0.05) * 20 - 3) < 0.22;
      if (crack) c = r() < 0.5 ? [196, 78, 30] : [120, 44, 22];
      if (r() < 0.015) c = shade(c, -34);
      return c;
    },
  },
  // Wrought iron of the braziers.
  iron: {
    size: 16,
    paint(x, y, r) {
      return shade([62, 56, 60], (y % 4 === 0 ? 14 : 0) + (x % 8 === 0 ? -12 : 0) + (r() - 0.5) * 12);
    },
  },
  // Bark of the dead trees: dry, grey-brown, vertical cracks.
  bark: {
    size: 16,
    paint(x, y, r) {
      const groove = (x + Math.floor(y / 5)) % 5 === 0;
      return shade(groove ? [52, 40, 36] : [104, 86, 74], (r() - 0.5) * 18);
    },
  },
  // The Acheron: murky dark water, a few red reflections.
  water: {
    size: 32,
    paint(x, y, r) {
      const wave = Math.sin(x * 0.4 + y * 0.15) + Math.sin(y * 0.55 - x * 0.1);
      let c: RGB = shade([28, 30, 38], wave * 6 + (r() - 0.5) * 6);
      if (wave > 1.6 && r() < 0.5) c = [120, 40, 30];
      return c;
    },
  },
  // Deep pit and moat walls: almost black earth.
  dark: {
    size: 16,
    paint(_x, y, r) {
      return shade([34, 26, 30], (y % 8 === 0 ? -6 : 0) + (r() - 0.5) * 10);
    },
  },
};

/**
 * Surfaces painted in PixelLab (D39), composed by tools/sprites/import_tileset.py:
 * served from /proto2d/tex/, with the metres one repeat covers. A surface
 * listed here replaces the one drawn in code.
 */
const IMAGES: Partial<Record<SurfaceKind, { file: string; metres: number }>> = {
  // New 128 px surfaces: finer, calmer ground to keep fighters readable.
  ash: { file: "/proto2d/inferno-rich/ground-0.png", metres: 4 },
  // Basalt matches the new structures; the previous sheets remain in public/tex.
  rock: { file: "/proto2d/inferno-rich/basalt-0.png", metres: 4 },
  brick: { file: "porta.png", metres: 16 },
  water: { file: "/proto2d/inferno-rich/river-0.png", metres: 8 },
};

/**
 * How much the tiling of a surface must be multiplied when it comes from an
 * image: callers tile as if it were the code-drawn texture (its size in metres).
 */
export function surfaceRepeat(kind: SurfaceKind): number {
  const image = IMAGES[kind];
  return image ? PAINTERS[kind].size / TEXELS_PER_METRE / image.metres : 1;
}

/** A new pixel texture: each surface sets its own tiling (they are tiny, 16–64 px). */
export function surfaceTexture(scene: Scene, kind: SurfaceKind): Texture {
  const painted = IMAGES[kind];
  if (painted) {
    const texture = new Texture(painted.file.startsWith('/')?painted.file:`/proto2d/tex/${painted.file}`, scene, true, true, Texture.NEAREST_SAMPLINGMODE);
    texture.wrapU = Texture.WRAP_ADDRESSMODE;
    texture.wrapV = Texture.WRAP_ADDRESSMODE;
    return texture;
  }
  const painter = PAINTERS[kind];
  const size = painter.size;
  const texture = new DynamicTexture(`surface-${kind}`, { width: size, height: size }, scene, false, Texture.NEAREST_SAMPLINGMODE);
  const ctx = texture.getContext() as unknown as CanvasRenderingContext2D;
  const image = ctx.createImageData(size, size);
  const random = rng(kind.length * 977 + size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const [cr, cg, cb] = painter.paint(x, y, random);
      const i = (y * size + x) * 4;
      image.data[i] = cr;
      image.data[i + 1] = cg;
      image.data[i + 2] = cb;
      image.data[i + 3] = 255;
    }
  }
  ctx.putImageData(image, 0, 0);
  texture.update(false);
  texture.wrapU = Texture.WRAP_ADDRESSMODE;
  texture.wrapV = Texture.WRAP_ADDRESSMODE;
  return texture;
}

/** Texels per metre of every surface. */
export const TEXELS_PER_METRE = 16;
